import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isBefore } from 'date-fns';
import parseSafeDate from 'utils/date/parseSafeDate';
import getFullCountdown from 'utils/date/getFullCountdown';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import { LanguageCode } from '@kouto/types';
import useTimer from './useTimer';

enum TicketSalesState {
  NOT_YET_OPEN = 'notYetOpen',
  OPEN = 'open',
  CLOSED = 'closed',
}

interface TimeStatusReturn {
  readonly state: TicketSalesState;
  readonly countdown: {
    readonly full: string;
    readonly highlight: string;
  };
  readonly static: {
    readonly full: string;
    readonly highlight: string;
  };
}

interface DateConfig {
  readonly openAt: Date | null;
  readonly closeAt: Date | null;
}

const DATE_FORMAT = 'MMMM do, h:mm a';

const formatStaticDate = (date: Date, locale?: LanguageCode): string => {
  const formattedDate = format(date, DATE_FORMAT);
  const timezone =
    new Intl.DateTimeFormat(locale, {
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return `${formattedDate} ${timezone}`;
};

const getCurrentState = (
  now: Date,
  { openAt, closeAt }: DateConfig,
): TicketSalesState | null => {
  if (!openAt || !closeAt) {
    return null;
  }

  if (now < openAt) {
    return TicketSalesState.NOT_YET_OPEN;
  }

  if (now >= openAt && now < closeAt) {
    return TicketSalesState.OPEN;
  }

  return TicketSalesState.CLOSED;
};

const createNotYetOpenResult = (
  now: Date,
  openAt: Date,
  t: (key: string) => string,
  locale?: LanguageCode,
): TimeStatusReturn => {
  const countdown = getFullCountdown(now, openAt);
  const staticDate = formatStaticDate(openAt, locale);

  return {
    state: TicketSalesState.NOT_YET_OPEN,
    countdown: {
      full: `${t('ticketSalesOpenIn')} ${countdown}`,
      highlight: countdown,
    },
    static: {
      full: `${t('ticketSalesOpen')} ${staticDate}`,
      highlight: staticDate,
    },
  };
};

const createOpenResult = (
  now: Date,
  closeAt: Date,
  t: (key: string) => string,
  locale?: LanguageCode,
): TimeStatusReturn => {
  const countdown = getFullCountdown(now, closeAt);
  const staticDate = formatStaticDate(closeAt, locale);

  return {
    state: TicketSalesState.OPEN,
    countdown: {
      full: `${t('ticketSalesCloseIn')} ${countdown}`,
      highlight: countdown,
    },
    static: {
      full: `${t('ticketSalesClose')} ${staticDate}`,
      highlight: staticDate,
    },
  };
};

const createClosedResult = (
  closeAt: Date,
  t: (key: string) => string,
  locale?: LanguageCode,
): TimeStatusReturn => {
  const staticDate = formatStaticDate(closeAt, locale);
  const closedText = t('ticketSalesClosed');

  return {
    state: TicketSalesState.CLOSED,
    countdown: {
      full: closedText,
      highlight: closedText,
    },
    static: {
      full: `${closedText} ${staticDate}`,
      highlight: staticDate,
    },
  };
};

const useLimitedTimeStatus = (
  ticketSalesOpenAt?: string | null,
  ticketSalesCloseAt?: string | null,
): TimeStatusReturn | null => {
  const { t } = useTranslation();
  const supportedLanguage = useSupportedLanguage();

  const dateConfig = useMemo(
    (): DateConfig => ({
      openAt: parseSafeDate(ticketSalesOpenAt),
      closeAt: parseSafeDate(ticketSalesCloseAt),
    }),
    [ticketSalesOpenAt, ticketSalesCloseAt],
  );

  const { openAt, closeAt } = dateConfig;

  const shouldRunTimer = () => {
    if (!openAt || !closeAt) {
      return false;
    }
    const tempNow = new Date();
    return isBefore(tempNow, closeAt);
  };

  const now = useTimer(shouldRunTimer());
  const currentState = getCurrentState(now, dateConfig);

  if (!openAt || !closeAt || !currentState) {
    return null;
  }

  switch (currentState) {
    case TicketSalesState.NOT_YET_OPEN:
      return createNotYetOpenResult(now, openAt, t, supportedLanguage);

    case TicketSalesState.OPEN:
      return createOpenResult(now, closeAt, t, supportedLanguage);

    case TicketSalesState.CLOSED:
      return createClosedResult(closeAt, t, supportedLanguage);

    default:
      return createClosedResult(closeAt, t, supportedLanguage);
  }
};

export { TicketSalesState };
export type { TimeStatusReturn };
export default useLimitedTimeStatus;
